import { useId } from 'react';
import { BRAND_LIMITS, WORKSPACE_DETAIL_FIELDS, type BrandProfileView } from '@mg/shared';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Field';
import { WorkspaceNameConflictNotice } from '../../../components/workspace/WorkspaceNameConflictNotice';
import { SECTION_COPY } from '../../brand/brandCopy';
import { BrandFieldShell } from '../../brand/fields/BrandFieldShell';
import { ReadOnlyField } from '../../brand/fields/ReadOnlyField';
import { brandFieldDescribedBy } from '../../brand/fields/fieldParts';
import { BRAND_SECTION_IDS } from '../../brand/editPages';
import { EditSection } from '../EditSection';
import type { BrandInformationFormState } from './useBrandInformationForm';

/**
 * Brand Workspace Details (spec:170, :213-214), at the very top of the Brand information page, outside
 * the Brand information card: the workspace's name and its public website, both required. Editable by
 * an admin, text for a member. The name is sent only when it changed; the website always, and while it
 * is not a valid address (a stored one included, O15) the page cannot be saved.
 */
export function WorkspaceDetailsCard({
  form,
  view,
  canEdit,
}: {
  form: BrandInformationFormState;
  view: BrandProfileView;
  canEdit: boolean;
}) {
  const nameId = useId();
  const websiteId = useId();
  const copy = SECTION_COPY.workspaceDetails;
  const nameConflict = form.problem?.kind === 'name' ? form.problem.conflict : null;

  return (
    <EditSection page="brand" id={BRAND_SECTION_IDS.workspaceDetails}>
      <Card>
        <CardHeader title={copy.title} description={copy.description} />
        {canEdit ? (
          <CardBody className="space-y-5">
            <BrandFieldShell id={nameId} label={WORKSPACE_DETAIL_FIELDS.name.label} sources={[]} error={form.nameError}>
              <Input
                id={nameId}
                value={form.name}
                required
                maxLength={BRAND_LIMITS.workspaceName}
                autoComplete="off"
                disabled={form.saving}
                aria-invalid={form.nameError ? true : undefined}
                aria-describedby={brandFieldDescribedBy(nameId, { error: form.nameError })}
                onChange={(e) => form.setName(e.target.value)}
              />
            </BrandFieldShell>
            {nameConflict && <WorkspaceNameConflictNotice conflict={nameConflict} />}
            <BrandFieldShell
              id={websiteId}
              label={WORKSPACE_DETAIL_FIELDS.websiteUrl.label}
              sources={[]}
              error={form.websiteError}
            >
              <Input
                id={websiteId}
                // Text, not url: an address typed without https:// is fine here and is completed on blur.
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="https://www.yourbrand.com"
                value={form.website.value}
                required
                disabled={form.saving}
                aria-invalid={form.websiteError ? true : undefined}
                aria-describedby={brandFieldDescribedBy(websiteId, { error: form.websiteError })}
                onChange={(e) => form.website.setValue(e.target.value)}
                onBlur={form.website.onBlur}
              />
            </BrandFieldShell>
          </CardBody>
        ) : (
          <CardBody className="py-1">
            <dl className="divide-y divide-line-soft">
              <ReadOnlyField label={WORKSPACE_DETAIL_FIELDS.name.label} value={view.workspace.name} sources={[]} kind="text" />
              <ReadOnlyField
                label={WORKSPACE_DETAIL_FIELDS.websiteUrl.label}
                value={view.workspace.websiteUrl}
                sources={[]}
                kind="text"
              />
            </dl>
          </CardBody>
        )}
      </Card>
    </EditSection>
  );
}
